import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { PLANS } from '../lib/plans.js';
import { FAQS } from '../content/faqs.js';

/**
 * Public marketing landing for markaii.com.
 *
 * Logged-in visitors get bounced to /dashboard so they don't sit on
 * the marketing pitch they've already bought into. Logged-out visitors
 * see the full hero + features + how-it-works + pricing.
 */
export default function Landing() {
  const { isAuthenticated } = useAuth();
  const [cadence, setCadence] = useState('monthly');

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="landing-shell">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-brand">
            <img src="/icons/markaii-m.png?v=2" alt="" className="brand-icon" />
            <span>Markaii</span>
          </div>
          <nav className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How it works</a>
            <a href="#pricing">Pricing</a>
            <Link to="/login" className="landing-nav-signin">Sign in</Link>
            <Link to="/signup" className="btn btn-primary btn-compact">Start free trial</Link>
          </nav>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-container">
          <p className="landing-eyebrow">Multi-brand AI marketing operating system</p>
          <h1 className="landing-headline">
            Marketing on autopilot —<br />for every brand you run.
          </h1>
          <p className="landing-subhead">
            Markaii generates marketing plans, writes platform-specific content, and publishes
            across Instagram, LinkedIn, X, and more — with a built-in AI assistant that runs the
            whole app by chat. One dashboard for every brand in your portfolio. Zero copy-paste.
          </p>
          <div className="landing-cta-row">
            <Link to="/signup" className="btn btn-primary btn-lg">Start free trial</Link>
            <Link to="/login" className="btn-outline">Sign in</Link>
          </div>
          <p className="landing-cta-note">14-day free trial · $0 today · cancel anytime before the trial ends</p>

          <div className="landing-hero-card">
            <div className="hero-card-bar">
              <span className="hero-dot dot-red" />
              <span className="hero-dot dot-yellow" />
              <span className="hero-dot dot-green" />
              <span className="hero-card-url">markaii.com / dashboard</span>
            </div>
            {/* Real product screenshot (not a mock) — captured from the live
                dashboard. Swap the file at public/screenshots/dashboard.png
                to refresh; keep it PII-free and content-rich. */}
            <img
              src="/screenshots/dashboard.png"
              alt="The Markaii dashboard — every brand's pillars, posts, and publish status in one view"
              className="hero-card-screenshot"
              width="800"
              height="722"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="landing-section" id="features">
        <div className="landing-container">
          <h2 className="landing-h2">What Markaii does</h2>
          <div className="feature-grid">
            <div className="feature-card feature-card-highlight">
              <div className="feature-icon">✧</div>
              <span className="feature-badge">New</span>
              <h3>An assistant that runs the app for you</h3>
              <p>
                A built-in AI assistant sits on every page. Ask in plain language — “open the
                inbox”, “show this brand’s posts”, “which posts are awaiting review?” — and it takes
                you straight there and answers about what’s on screen. It never posts, sends, or
                changes anything on its own; you stay in control.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">✦</div>
              <h3>Plans, not just posts</h3>
              <p>
                AI builds a real marketing plan for each brand — audience, voice, content pillars,
                cadence, KPIs. It updates the plan automatically as the numbers come in.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">◇</div>
              <h3>Every platform, native</h3>
              <p>
                One draft becomes Instagram carousels, Reels, Stories, LinkedIn posts, X threads,
                TikTok scripts, blog posts, and email — each formatted for its platform's rules.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">◎</div>
              <h3>Approve once, publish everywhere</h3>
              <p>
                Drafts land in your inbox or dashboard with a one-click approve link. Markaii
                publishes via each platform's API, on schedule, with idempotency and retries.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">△</div>
              <h3>Stats that close the loop</h3>
              <p>
                Engagement data flows back in from every platform. Markaii reads what worked,
                proposes plan tweaks, and shows you cross-brand patterns in one view.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⬡</div>
              <h3>Multiple brands, one workspace</h3>
              <p>
                Run as many brands as you have SaaS apps. Each has its own voice, hard
                constraints, social accounts, and team — all under one billing account.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">◐</div>
              <h3>Your brand, never the bot's</h3>
              <p>
                Every brand defines hard constraints — competitor names that can't appear, claims
                you'll never make, words your voice doesn't use. The AI obeys them by design.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-alt" id="how-it-works">
        <div className="landing-container">
          <h2 className="landing-h2">How it works</h2>
          <ol className="how-list">
            <li>
              <span className="how-number">1</span>
              <div>
                <h3>Define a brand</h3>
                <p>
                  Positioning, voice, audience, hard constraints, brand colors. Once. Markaii
                  handles every brand independently from there.
                </p>
              </div>
            </li>
            <li>
              <span className="how-number">2</span>
              <div>
                <h3>Markaii generates the plan + content</h3>
                <p>
                  AI builds a per-brand marketing plan and drafts platform-specific content on the
                  cadence the plan calls for. You see everything before anything goes live.
                </p>
              </div>
            </li>
            <li>
              <span className="how-number">3</span>
              <div>
                <h3>Approve, publish, learn</h3>
                <p>
                  Approve via email or dashboard. Markaii publishes, ingests engagement, and
                  iterates the plan against your KPIs — week over week.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Pricing                                                             */}
      {/* ------------------------------------------------------------------ */}
      <section className="landing-section" id="pricing">
        <div className="landing-container">
          <h2 className="landing-h2">Simple, transparent pricing</h2>
          <p className="landing-pricing-sub">
            Markaii replaces a full-time marketer — priced to reflect that.
            One subscription covers all brands on your plan.
          </p>

          <div className="billing-cadence-toggle landing-cadence-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={cadence === 'monthly'}
              className={`billing-cadence-btn ${cadence === 'monthly' ? 'active' : ''}`}
              onClick={() => setCadence('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cadence === 'annual'}
              className={`billing-cadence-btn ${cadence === 'annual' ? 'active' : ''}`}
              onClick={() => setCadence('annual')}
            >
              Annual <span className="billing-cadence-badge">−17%</span>
            </button>
          </div>

          <div className="billing-plans landing-plans">
            {PLANS.map((plan) => {
              const display = plan[cadence];
              return (
                <div
                  key={plan.tier}
                  className={`billing-plan-card ${plan.recommended ? 'recommended' : ''}`}
                >
                  {plan.recommended && (
                    <span className="billing-plan-badge">Most popular</span>
                  )}
                  <h4>{plan.name}</h4>
                  <div className="plan-price">
                    {display.price}{' '}
                    <span className="plan-price-sub">{display.sub}</span>
                  </div>
                  {cadence === 'annual' && display.save && (
                    <div className="plan-price-save">{display.save}</div>
                  )}
                  <p className="plan-blurb">{plan.blurb}</p>
                  <ul>
                    {plan.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <Link
                    to={`/signup?plan=${plan.tier}&cadence=${cadence}`}
                    className={`btn ${plan.recommended ? 'btn-primary' : ''} btn-block`}
                  >
                    Start free trial
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="landing-pricing-footnote">
            Need more than 10 brands?{' '}
            <a href="mailto:sales@markaii.com?subject=Agency+scale+inquiry">Contact sales</a>
            {' '}— Agency extends to 15 brands with a custom contract. Above 15 we set up
            enterprise pricing. All plans include a 14-day free trial.
          </p>
        </div>
      </section>

      {/* FAQ — answer-shaped copy that stands alone when quoted out of
          context (AEO: retrieval systems lift FAQ answers verbatim when
          answering "is there a tool that..." questions). Content lives
          in src/content/faqs.js — same source as the FAQPage JSON-LD
          injected into index.html by vite.config.js. */}
      <section className="landing-section landing-faq-section" id="faq">
        <div className="landing-container">
          <h2 className="landing-h2">Frequently asked questions</h2>
          <div className="landing-faq-list">
            {FAQS.map((f) => (
              <details key={f.q} className="landing-faq-item">
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Social Media ─── */}
      <section className="landing-section landing-section-alt landing-social-section">
        <div className="landing-container">
          <h2 className="landing-h2">Follow Markaii</h2>
          <p className="landing-social-sub">
            Marketing updates, product drops, and the occasional take on AI&nbsp;+ content.
          </p>
          <div className="landing-social-links">
            <a
              href="https://x.com/markaiiofficial"
              className="landing-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Markaii on X"
            >
              <svg className="landing-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.734-8.835L1.254 2.25H8.08l4.259 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              X / Twitter
            </a>
            <a
              href="https://facebook.com/markaiiofficial"
              className="landing-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Markaii on Facebook"
            >
              <svg className="landing-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </a>
            <a
              href="https://instagram.com/markaiiofficial"
              className="landing-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Markaii on Instagram"
            >
              <svg className="landing-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Instagram
            </a>
            <a
              href="https://tiktok.com/@markaiiofficial"
              className="landing-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Markaii on TikTok"
            >
              <svg className="landing-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
              TikTok
            </a>
            <a
              href="https://linkedin.com/company/markaii"
              className="landing-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Markaii on LinkedIn"
            >
              <svg className="landing-social-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      <section className="landing-section landing-cta-section">
        <div className="landing-container">
          <h2 className="landing-h2">Ready to put marketing on autopilot?</h2>
          <p className="landing-cta-sub">
            Start your 14-day free trial — $0 today, cancel anytime before the trial ends.
          </p>
          <div className="landing-cta-row landing-cta-row-center">
            <Link to="/signup" className="btn btn-primary btn-lg">Start free trial</Link>
            <Link to="/login" className="btn-outline">Sign in</Link>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <span className="landing-brand-mini">Markaii</span>
          <span className="landing-footer-meta">
            <Link to="/privacy">Privacy</Link>
            {' · '}
            <Link to="/terms">Terms</Link>
            {' · '}
            <Link to="/data-deletion">Data deletion</Link>
            {' · '}
            © {new Date().getFullYear()} markaii.com
          </span>
        </div>
      </footer>
    </div>
  );
}
